/**
 * Technological cards at a visit.
 *
 * A card may still list a good the salon has deleted (ygoods.deleted — the
 * dashboard shows it greyed until the card is edited). Such a good takes no new
 * write-offs: the backend refuses them, so "add from card" leaves it out.
 */
import type { TechnologicalCardItem } from "@/lib/types/appointment";

/** The card items that can be written off now (deleted goods left out). */
export function applicableCardItems<T extends Pick<TechnologicalCardItem, "good">>(items: T[] | null | undefined): T[] {
  return (items ?? []).filter((item) => !item.good?.deleted);
}

/**
 * The cost per write-off unit of a line, as the consumables tab needs it to
 * show a line total (unit × amount). Boona: storage plan ST1.
 *
 * A visit's recorded line: `price` is a UNIT price (the visit history shows
 * price × amount); newer backends also send it as `unit_price`.
 */
export function recordedUnitPrice(item: { unit_price?: number | null; price?: number | null }): number {
  return Number(item.unit_price ?? item.price ?? 0);
}

/**
 * A tech card line: newer backends send `unit_price` (the good's cost per
 * unit) and `price` = amount × unit. Older ones sent only `price`, a line
 * total for cards imported from YClients.
 */
export function cardItemUnitPrice(item: { unit_price?: number | null; price?: number | null; amount?: number | null }): number {
  if (item.unit_price != null) return Number(item.unit_price);
  const amount = item.amount || 1;
  const price = Number(item.price ?? 0);
  return amount > 0 ? price / amount : price;
}
